const runtimeConfigWindowKey = '__SCOUT_RUNTIME_CONFIG__'

/**
 * Whether the env-gated end-to-end auth bypass is active.
 *
 * Set to `true` only by the Playwright smoke harness (`VITE_E2E_BYPASS_AUTH=true`
 * on the vite dev server it boots — see `playwright.config.ts`). When active,
 * `RequireAuth` renders protected content without redirecting to Auth0 and the
 * RTK Query base query skips the bearer-token fetch, so the smoke can exercise
 * the real app shell without a live Auth0 session. It mirrors outpatient's
 * `shouldBypassAuthForE2E`. The env var is unset in normal dev and in every
 * production build, so this constant is `false` and the bypass is fully inert.
 */
export const shouldBypassAuthForE2E =
  import.meta.env.VITE_E2E_BYPASS_AUTH === 'true'

export interface RuntimeConfig {
  apiBaseUrl?: string
  auth?: {
    audience?: string
    clientId?: string
    domain?: string
  }
  products?: {
    arc?: {
      enabled?: boolean
      url?: string
    }
  }
}

type RuntimeConfigWindow = Window & {
  [runtimeConfigWindowKey]?: RuntimeConfig
}

class RegardConfigError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'RegardConfigError'
  }
}

const getStoredRuntimeConfig = (): RuntimeConfig | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }
  return (window as RuntimeConfigWindow)[runtimeConfigWindowKey]
}

export const setRuntimeConfig = (config: RuntimeConfig): void => {
  if (typeof window !== 'undefined') {
    const runtimeWindow = window as RuntimeConfigWindow
    runtimeWindow[runtimeConfigWindowKey] = config
  }
}

export const clearRuntimeConfig = (): void => {
  if (typeof window !== 'undefined') {
    delete (window as RuntimeConfigWindow)[runtimeConfigWindowKey]
  }
}

export const getRuntimeConfig = (): RuntimeConfig => {
  const config = getStoredRuntimeConfig()
  if (config) {
    return config
  }
  throw new RegardConfigError('Runtime config not loaded')
}

export const getApiBaseUrl = (): string => {
  const configuredBaseUrl = getStoredRuntimeConfig()?.apiBaseUrl?.trim()
  if (configuredBaseUrl) {
    return configuredBaseUrl
  }
  // Default to the same-origin `/api` prefix, resolved to an absolute URL so
  // fetch can parse it. In dev and smoke tests the vite proxy forwards
  // `/api/*` to the outpatient backend (stripping the prefix), matching on the
  // path regardless of host. Deploys that talk to a different-origin backend set
  // an absolute `apiBaseUrl` in runtime config to override this.
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api`
  }
  return '/api'
}

export interface AuthConfig {
  audience?: string
  clientId: string
  domain: string
}

export const getAuthConfig = (): AuthConfig => {
  const auth = getStoredRuntimeConfig()?.auth
  const domain = auth?.domain?.trim()
  const clientId = auth?.clientId?.trim()
  if (!domain || !clientId) {
    throw new RegardConfigError('Auth0 configuration missing: domain/clientId')
  }
  return { audience: auth?.audience?.trim() || undefined, clientId, domain }
}

export interface ProductsConfig {
  arc: {
    enabled: boolean
    url: string
  }
}

/**
 * Resolves the sibling-product configuration used by the header's product
 * switcher.
 *
 * Mirrors arc's `getProductsConfig`, inverted: arc is the sibling here. Missing
 * or partial config resolves to disabled with a blank URL rather than throwing,
 * because the switcher hides itself when there is nowhere to switch to — a
 * missing product host is a degraded header, not a boot failure.
 */
export const getProductsConfig = (): ProductsConfig => {
  const arc = getStoredRuntimeConfig()?.products?.arc
  return {
    arc: { enabled: arc?.enabled === true, url: arc?.url?.trim() || '' },
  }
}
